import moment from "moment";
import { getUITranslation } from "../utils/get_ui_translation";
import { ppNumber } from "../framework/utils";
import { loadData } from "../utils/chunk_load";

/* global _money */

const fetchDataSource = async (baseUrl, basicFilters, transformPage) => {
  const basicPath = baseUrl + "?" + buildFiltersQueryParams(basicFilters);

  // The academy filter is not treated as a basic filter
  // because it changes how the data is fetched
  const academyFilter = document.querySelector("select[name=academy_filter]")?.value;

  if (!academyFilter) {
    // Shortcut for single academy data fetching
    return loadData(basicPath, transformPage);
  } else if (academyFilter !== "all") {
    // Shortcut for single academy within a super academy data fetching
    return loadData(basicPath + "&academy_id=" + academyFilter, transformPage);
  } else if (academyFilter == "all") {
    // Fetch data for all academies in the super academy context
    // Do N x M queries for N academies and M pages by academy depending on their number of training_session
    // The promise resolves when all queries are done
    const academyIds = JSON.parse(document.querySelector("input[name=academy_ids]").value);
    const promises = academyIds.map((id) => loadData(basicPath + "&academy_id=" + encodeURIComponent(id), transformPage));

    return Promise.all(promises).then((it) => it.flat());
  }
};

const buildFiltersQueryParams = (filters) => {
  return filters
    .map((it) => {
      const input = document.querySelector(`*[name=${it}]`);
      if (input) {
        return `${it}=${encodeURIComponent(input.value)}`;
      } else {
        return "";
      }
    })
    .join("&");
};

function createChart(Chart, reportConfig, height) {
  return new Chart(reportConfig.dataChartTag, {
    series: [
      {
        type: "steparea",
        argumentField: "date",
        valueField: "totalCharges",
        name: getUITranslation("Charges"),
        color: reportConfig.colors.red,
        hoverMode: "none",
      },
      {
        type: "steparea",
        argumentField: "date",
        valueField: "totalInvoices",
        name: getUITranslation("Factures"),
        color: reportConfig.colors.green,
        hoverMode: "none",
      },
      {
        type: "stepline",
        argumentField: "date",
        valueField: "total",
        name: getUITranslation("Factures - Charges"),
        color: reportConfig.colors.default,
        hoverMode: "none  ",
        point: {
          visible: false,
        },
      },
    ],
    tooltip: {
      enabled: true,
      customizeTooltip: function (x) {
        return { html: `<span>${x.seriesName}: ${ppNumber(_.round(x.value, 2))}${_money.symbol}</span>` };
      },
    },
    valueAxis: {
      label: {
        customizeText: (x) => `${ppNumber(x.value)} ${_money.symbol}`,
      },
    },
    argumentAxis: {
      label: {
        customizeText: function (x) {
          return x.value;
        },
      },
    },
    size: {
      height: height,
    },
    legend: {
      visible: true,
    },
    adaptiveLayout: {
      width: 450,
    },
    onInitialized: function () {
      if (document.querySelector(".js-training-session-finance")) {
        initChartFromSession(reportConfig);
      }
    },
  });
}

function initChartFromSession(reportConfig) {
  /*
    We need all invoices and charges for the chart, and we need to overwrite
    start_date and end_date that might be params of the request (those are used for BPF)
  */
  const startDate = "1900-01-01";
  const endDate = "3000-01-01";

  const promises = [p`/ajax/finance/tabular_chart_invoices`, p`/ajax/finance/tabular_chart_charges`]
    .map((baseUrl) => `${baseUrl}?training_session_id=${window.training_session_id}&start_date=${startDate}&end_date=${endDate}`)
    .map((url) => loadData(url));

  Promise.all(promises)
    .then(([invoices, charges]) => {
      const data = [].concat(invoices).concat(charges);
      setChartData(data, reportConfig);
    })
    .catch((e) => {
      console.error("Error while fetching page", e);
    });
}

function setChartData(data, reportConfig) {
  let totalCharges = 0;
  let totalInvoices = 0;
  let totalPayed = 0;
  let total = 0;
  let payed = 0;
  let outstanding = 0;
  let maxDate;

  const dateFormat = "MM-YYYY";
  const items = data.sort((a, b) => (a.date >= b.date ? 1 : -1));

  const dataSource = items.reduce(function (acc, row) {
    if (!maxDate || moment(row.date).isAfter(maxDate)) {
      maxDate = moment(row.date);
    }
    total += row.total;

    // type may be in ["Facture", "Avoir", "Avoir interne", "Charge"]
    if (row.type == "Charge") {
      totalCharges += row.total;
    } else {
      totalInvoices += row.total;
      // charges are not considered in the total payed
      totalPayed += row.payed_total;
    }
    if (row.type == "Facture" && row.status == "Payé") payed += 1;
    if (row.type == "Facture" && row.status == "En retard") outstanding += 1;

    // If you find this unusual don't panic. The items array is already sorted so we just need to check if the last value inserted
    // in the accumulator is equal to the date of the items row being treated.
    const lastIndex = acc.length - 1;
    if (acc[lastIndex] && acc[lastIndex].date == moment(row.date).format(dateFormat)) {
      acc[lastIndex].totalInvoices = totalInvoices;
      acc[lastIndex].totalCharges = totalCharges;
      acc[lastIndex].total = total;
    } else {
      acc.push({ date: moment(row.date).format(dateFormat), totalInvoices: totalInvoices, totalCharges: totalCharges, total: total });
    }
    return acc;
  }, []);

  // At this point if we have a maxDate set we can add the last bit
  if (maxDate) {
    // Trick to avoid a vertical line on the last month the user invoiced someone.
    dataSource.push({
      date: moment(maxDate).add(1, "month").format(dateFormat),
      totalInvoices: totalInvoices,
      totalCharges: totalCharges,
      total: total,
    });
  }

  // Update the chart datasource
  reportConfig.dataChart.option("dataSource", dataSource);

  // Update the balance
  const updateInnerHtml = (selector, value, rounding = false) => {
    const element = document.querySelector(selector);
    if (element) {
      element.innerHTML = rounding ? ppNumber(_.round(value, 2)) : value;
    }
  };
  updateInnerHtml(".js-invoices-minus-charges", total, true); // invoices - charges
  updateInnerHtml(".js-charges-total", totalCharges, true);
  updateInnerHtml(".js-cashed", totalPayed, true);
  updateInnerHtml(".js-number-paid-invoices", payed);
  updateInnerHtml(".js-number-late-invoices", outstanding);
}

export { fetchDataSource, createChart, setChartData };
